import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

let shouldAutoOpenCartDrawer = false;
let keepCartDrawerOpen = false;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let clearLoadingTimeoutId;

// Custom pre-fetch signal dispatched by product-form before the cart add fetch starts.
// product-form.js must dispatch: document.dispatchEvent(new CustomEvent('cart:adding'))
const CART_ADDING_EVENT = 'cart:adding';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('disable-outside-close', '');
    this.setAttribute('disable-escape-close', '');

    document.addEventListener(CART_ADDING_EVENT, this.#handleCartAdding);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartSettled);

    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.refs.dialog?.addEventListener('click', this.#handleBackdropClick);
    this.refs.dialog?.addEventListener('close', this.#handleNativeClose);
    this.refs.dialog?.addEventListener('cancel', this.#handleNativeCancel);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // If section morph replaced the old drawer during an in-flight add,
    // immediately re-open this new instance with loader.
    if (this.hasAttribute('auto-open') && shouldAutoOpenCartDrawer) {
      keepCartDrawerOpen = true;
      this.refs.dialog?.setAttribute('loading', '');
      this.showDialog();
      this.#startLoadingFallbackTimer();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CART_ADDING_EVENT, this.#handleCartAdding);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartSettled);

    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.refs.dialog?.removeEventListener('click', this.#handleBackdropClick);
    this.refs.dialog?.removeEventListener('close', this.#handleNativeClose);
    this.refs.dialog?.removeEventListener('cancel', this.#handleNativeCancel);

    this.#historyAbortController?.abort();
  }

  #handleDialogOpen = () => {
    document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
  };

  #handleNativeCancel = () => {};

  #handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const rect = dialog.getBoundingClientRect();
    const outsidePanel =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (outsidePanel) {
      this.close();
    }
  };

  #handleNativeClose = () => {
    if (!keepCartDrawerOpen) return;

    // If dialog gets closed by any non-explicit path (e.g. section morph), reopen.
    // Explicit closes go through close() which clears keepCartDrawerOpen first,
    // so by the time the native 'close' event fires, keepCartDrawerOpen is false
    // and this handler exits early above.
    requestAnimationFrame(() => {
      const activeDrawer = document.querySelector('cart-drawer-component[auto-open]');
      const drawer = activeDrawer instanceof CartDrawerComponent ? activeDrawer : this;
      drawer.showDialog();
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdding = () => {
    if (!this.hasAttribute('auto-open')) return;

    shouldAutoOpenCartDrawer = true;
    keepCartDrawerOpen = true;

    // Open immediately before fetch completes and show loading overlay.
    this.refs.dialog?.setAttribute('loading', '');
    this.#startLoadingFallbackTimer();
    this.showDialog();
  };

  /**
   * Handles cart add events - clears loading state, opens drawer if not already open,
   * and announces count to screen readers.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (shouldAutoOpenCartDrawer || this.refs.dialog?.hasAttribute('loading')) {
      this.#clearLoadingState();
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    const cartCount = /** @type {CustomEvent<{ resource?: { item_count?: number } }>} */ (event).detail?.resource?.item_count;
    this.#announceCartCount(cartCount);

    // #morphCartItemsOnly (in cart-items-component) syncs the dialog's cart-drawer--empty
    // class via the sections data in the CartAddEvent. This is a safety-net for the
    // fallback path (sectionRenderer.renderSection) which skips that sync because it
    // morphs the whole section rather than calling #morphCartItemsOnly.
    if (this.refs.dialog?.classList.contains('cart-drawer--empty')) {
      const dialog = this.refs.dialog;
      dialog.classList.remove('cart-drawer--empty');
      dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
      this.#updateStickyState();
    }
  };

  #handleCartSettled = () => {
    if (!this.hasAttribute('auto-open')) return;
    if (!shouldAutoOpenCartDrawer && !this.refs.dialog?.hasAttribute('loading')) return;

    this.#clearLoadingState();
  };

  #startLoadingFallbackTimer() {
    if (clearLoadingTimeoutId) {
      clearTimeout(clearLoadingTimeoutId);
    }

    // Safety: never leave drawer stuck in loading if upstream event does not fire.
    clearLoadingTimeoutId = setTimeout(() => {
      this.#clearLoadingState();
    }, 8000);
  }

  #clearLoadingState() {
    if (clearLoadingTimeoutId) {
      clearTimeout(clearLoadingTimeoutId);
      clearLoadingTimeoutId = undefined;
    }

    this.refs.dialog?.removeAttribute('loading');
    shouldAutoOpenCartDrawer = false;
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    keepCartDrawerOpen = false;
    shouldAutoOpenCartDrawer = false;
    this.#clearLoadingState();
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
